import { Theme, makeStyles } from '@material-ui/core';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';
import clsx from 'clsx';
import { WitboostPaletteColor } from '../../themes';
import { FC, HTMLAttributes, HTMLProps, ReactNode, forwardRef } from 'react';
import { WbUnstyledLink } from '../WbLink';

export type WbBadgeVariantProps =
  | {
      variant: 'soft';
      color?: WitboostPaletteColor;
    }
  | {
      variant: 'bright';
      color?: WitboostPaletteColor;
    }
  | {
      variant?: undefined;
      color?: WitboostPaletteColor;
    }
  | {
      variant: 'stroke';
      color?: WitboostPaletteColor;
    }
  | {
      variant: 'custom';
      color?: string;
    }
  | {
      variant: 'disabled';
    };

export type WbBadgeProps = HTMLProps<HTMLElement> &
  WbBadgeVariantProps & {
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    /**
     *WbBadge acts as a link bringing the user to the "to" location
     */
    to?: string;
    /**
     * WbBadge acts as a button executing the onClick callback
     */
    onClick?: HTMLProps<HTMLButtonElement>['onClick'];
  };

const DEFAULT_COLOR: WitboostPaletteColor = 'primary';

const useStyles = makeStyles<Theme, WbBadgeVariantProps>(
  theme => ({
    root: variantProps => {
      const baseStyle: React.CSSProperties = {
        border: 'none',
        borderRadius: theme.spacing(0.5),
        paddingInline: theme.spacing(0.75),
        ...theme.typography.body2,
        fontWeight: 500,
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        width: 'fit-content',
        maxWidth: '100%',
        minHeight: 24,
      };

      let variantStyle: React.CSSProperties = {};

      let iconColor: string;

      switch (variantProps.variant) {
        default:
        case 'stroke':
          variantStyle = {
            backgroundColor:
              theme.palette.bkg[variantProps.color ?? DEFAULT_COLOR],
            border: `1px ${
              theme.palette[variantProps.color ?? DEFAULT_COLOR].dark
            } solid`,
          };
          iconColor = theme.palette[variantProps.color ?? DEFAULT_COLOR].dark;
          break;
        case 'custom':
          variantStyle = {
            backgroundColor: variantProps.color,
          };
          break;
        case 'soft':
          variantStyle = {
            backgroundColor:
              theme.palette.softened[variantProps.color ?? DEFAULT_COLOR],
            border: `1px ${
              theme.palette[variantProps.color ?? DEFAULT_COLOR].dark
            } solid`,
          };
          break;
        case 'bright':
          variantStyle = {
            backgroundColor:
              theme.palette[variantProps.color ?? DEFAULT_COLOR].main,
            border: `1px ${
              theme.palette[variantProps.color ?? DEFAULT_COLOR].dark
            } solid`,
          };
          break;
        case 'disabled':
          variantStyle = {
            backgroundSize: '8px 8px',
            backgroundColor: theme.palette.blueGrey[100],
            backgroundImage: `repeating-linear-gradient(-45deg,${theme.palette.blueGrey[100]} 0,${theme.palette.blueGrey[100]} 4px,#b0b0b0 5px,#b0b0b0 5.5px)`,
          };
      }
      let textColor;
      // getContraxtText can throw
      try {
        textColor = theme.palette.getContrastText(
          variantStyle.backgroundColor ?? theme.palette.white,
        );
      } catch (_e) {
        textColor = theme.palette.black;
      }

      // default to contrast text for icon if not specified through a variant
      iconColor ??= textColor;

      return {
        ...baseStyle,
        color: textColor,
        ...(variantStyle as any),
        // target icon elements
        '& > :not($contentWrapper)': {
          color: iconColor,
        },
      };
    },
    interactive: {
      cursor: 'pointer',
      '&:hover': {
        filter: 'brightness(97%)',
      },
    },
    // dynamic class to be referenced by root class which is dynamic
    contentWrapper: () => ({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
  }),
  { name: 'WbBadge' },
);

export const WbBadge: FC<WbBadgeProps> = forwardRef<HTMLElement, WbBadgeProps>(
  (
    {
      className,
      children,
      startIcon,
      color,
      variant,
      endIcon,
      to,
      onClick,
      ...props
    },
    ref,
  ) => {
    const classes = useStyles({ color, variant } as WbBadgeVariantProps);

    const content = (
      <>
        {startIcon}
        {children && (
          <span className={clsx(classes.contentWrapper)}>{children}</span>
        )}
        {to ? endIcon ?? <LinkOutlinedIcon fontSize="small" /> : endIcon}
      </>
    );

    if (to)
      return (
        <WbUnstyledLink
          {...props}
          ref={ref}
          className={clsx(classes.root, classes.interactive, className)}
          to={to}
        >
          {content}
        </WbUnstyledLink>
      );

    if (onClick)
      return (
        <button
          type="button"
          {...(props as HTMLAttributes<HTMLButtonElement>)}
          className={clsx(classes.root, classes.interactive, className)}
          onClick={onClick}
          ref={ref as React.Ref<HTMLButtonElement>}
        >
          {content}
        </button>
      );

    return (
      <span {...props} ref={ref} className={clsx(classes.root, className)}>
        {content}
      </span>
    );
  },
) as FC<WbBadgeProps>;
